import { useMemo, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, GripVerticalIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import {
  GESPREK_DREMPEL_WEKEN_STANDAARD,
  mag,
  opdrachtenTekst,
  redenGeenKoppeling,
  type PersoonRij,
} from '@vip/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Item, ItemContent, ItemDescription, ItemGroup, ItemTitle } from '@/components/ui/item';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { useBeeindigKoppeling, useKoppel, usePersonen } from '../personen/personen.api';
import {
  doetMee,
  filterKaarten,
  kaarten,
  keten,
  past,
  staatStil,
  stilteTekst,
  zonderBuddy,
  type Kaart,
} from './begeleiding';

/** De rand die zegt: hier kan het neer. Twee standen, zodat de linkerkolom en
 *  de buddykaarten hetzelfde teken geven. */
const DROP_KAN = 'outline-primary/40 outline-1 outline-dashed outline-offset-2';
const DROP_RAAK = 'bg-primary/5 outline-primary outline-2 outline-offset-2';

/**
 * Wie wie begeleidt.
 *
 * Twee kolommen. Links de werklijst: wie nog geen buddy heeft. Rechts een kaart
 * per persoon die kan inloggen, met de mensen die hij begeleidt. Slepen van
 * links naar een kaart maakt de koppeling, slepen tussen kaarten verplaatst
 * iemand.
 *
 * Wie een buddy heeft stond links ook, in een tweede kaart. Dat was een tweede
 * afbeelding van wat rechts al staat, en daar staat het op de plek die het
 * antwoord geeft: onder wie hij hangt. Zoeken filtert allebei de kolommen, dus
 * iemand terugvinden gaat zonder die lijst.
 *
 * Dit was een tabblad onder Personen, gegroepeerd op opdracht. Dat liep vast op
 * de werkelijkheid: coach en gecoachte zitten vaak niet op dezelfde opdracht, en
 * een buddy wordt zelf ook begeleid. Dezelfde mens stond daardoor op drie
 * plekken. De buddy is nu de structuur en de opdracht is een regel tekst.
 *
 * Loskoppelen kan met het kruisje of door iemand terug te slepen naar links, en
 * vraagt in beide gevallen eerst. Verplaatsen niet: dat is een correctie waar
 * niets van verdwijnt, loskoppelen laat iemand zonder begeleiding achter.
 *
 * ### Wat een buddy hier mag
 *
 * Kijken: het hele bord, met de namen erop. Dat is waar het bord voor is.
 * Slepen alleen bij zijn eigen kaart, en daarop alleen iemand die nog geen
 * buddy heeft. Zonder die tweede voorwaarde zou hij iemand van een collega
 * kunnen weghalen, want koppelen hangt om. Dezelfde regel staat in
 * `worker/src/routes/personen.ts`; hier zorgt hij dat de kaart niet oplicht
 * voor iets dat toch geweigerd wordt.
 */
export function BegeleidingPagina() {
  const { data } = usePersonen();
  const { user } = useAuth();
  const magAlles = mag(user?.rechten, 'persoon:manage');
  const koppel = useKoppel();
  const beeindig = useBeeindigKoppeling();

  const nu = useMemo(() => new Date().toISOString(), []);
  const [zoek, setZoek] = useState('');
  const [sleept, setSleept] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [overLos, setOverLos] = useState(false);
  const [losTe, setLosTe] = useState<PersoonRij | null>(null);
  const [kiezer, setKiezer] = useState<Kiezer | null>(null);

  const drempel = data?.drempelWeken ?? GESPREK_DREMPEL_WEKEN_STANDAARD;
  const personen = useMemo(() => (data?.personen ?? []).filter(doetMee), [data?.personen]);
  const perId = useMemo(() => new Map(personen.map((p) => [p.id, p])), [personen]);
  const ketting = useMemo(() => keten(personen), [personen]);
  // Met de eigen id erbij: de kaart van wie kijkt hoort bovenaan te staan.
  const alleKaarten = useMemo(() => kaarten(personen, user?.id), [personen, user?.id]);

  const rechts = useMemo(() => filterKaarten(alleKaarten, zoek), [alleKaarten, zoek]);
  const geen = zonderBuddy(personen.filter((p) => past(p, zoek)));

  /** Dezelfde regels als de server, met de keten van dit scherm erbij. Een
   *  koppeling die er al is telt niet als fout maar levert ook niets op. */
  const magNaar = (mens: PersoonRij, buddy: PersoonRij) => {
    if (mens.buddy?.id === buddy.id) return false;
    // Een buddy pakt alleen iemand zonder buddy op, en alleen naar zichzelf.
    if (!magAlles && (buddy.id !== user?.id || mens.buddy !== null)) return false;
    return redenGeenKoppeling(buddy, mens, ketting) === null;
  };

  /** Loskoppelen mag bij je eigen gecoachten. De Admin overal. */
  const magLos = (mens: PersoonRij) => magAlles || mens.buddy?.id === user?.id;

  const gesleepte = sleept ? (perId.get(sleept) ?? null) : null;

  const pak = (id: string) => (e: DragEvent<HTMLElement>) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setSleept(id);
  };
  const laatLos = () => {
    setSleept(null);
    setOver(null);
    setOverLos(false);
  };
  const zetNeer = (buddy: PersoonRij) => {
    if (!gesleepte || !magNaar(gesleepte, buddy)) return;
    koppel.mutate({ buddyId: buddy.id, medewerkerId: gesleepte.id });
  };

  /** Terugslepen kan alleen bij wie nu een buddy heeft; bij de rest valt er
   *  niets te beëindigen. De kaart dimt niet als het niet kan, want het is ook
   *  de kaart waar je vandaan sleept. */
  const losbaar = Boolean(gesleepte?.buddy && gesleepte.koppelingId && magLos(gesleepte));
  const randLos = !losbaar ? '' : overLos ? DROP_RAAK : DROP_KAN;

  const fout = koppel.error ?? beeindig.error;

  return (
    <div className="flex flex-col gap-4">
      <InputGroup className="w-full sm:w-72">
        <InputGroupAddon>
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          aria-label="Zoek een naam"
          autoComplete="off"
          onChange={(e) => setZoek(e.target.value)}
          placeholder="Zoek een naam"
          value={zoek}
        />
      </InputGroup>

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout instanceof ApiError ? fout.message : String(fout)}</AlertTitle>
        </Alert>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <Kop aantal={geen.length} titel="Nog geen buddy" />
          <Card
            className={`overflow-hidden transition-colors lg:h-[calc(100svh-15rem)] ${randLos}`}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              if (overLos) setOverLos(false);
            }}
            onDragOver={(e) => {
              if (!losbaar) return;
              e.preventDefault();
              if (!overLos) setOverLos(true);
            }}
            onDrop={(e) => {
              e.preventDefault();
              const mens = gesleepte;
              laatLos();
              // Loskoppelen vraagt eerst, of je het met het kruisje doet of door
              // iemand hierheen terug te slepen.
              if (mens?.buddy && mens.koppelingId) setLosTe(mens);
            }}
          >
            <CardContent className="flex-1 overflow-y-auto">
              {geen.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {zoek.trim() === ''
                    ? 'Iedereen heeft een buddy.'
                    : 'Niemand zonder buddy gevonden.'}
                </p>
              ) : (
                <ItemGroup>
                  {geen.map((p) => (
                    <MensRij
                      key={p.id}
                      onKies={() => setKiezer({ soort: 'mens', persoon: p })}
                      onLos={laatLos}
                      onPak={pak(p.id)}
                      p={p}
                      sleept={sleept === p.id}
                    />
                  ))}
                </ItemGroup>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <Kop aantal={rechts.length} titel="Buddy's" />
          {/* Een schuifgebied knipt alles af wat buiten zijn rand valt, en dat
              is precies waar de rand van een kaart ligt: de lijn eromheen, en
              bij het slepen de stippellijn nog vier pixels verder naar buiten.
              Vandaar acht pixels lucht binnenin. Die gaat er aan de buitenkant
              weer af (`-m-2`) en zit in de hoogte verrekend (een rem meer dan de
              linkerkolom), zodat de kaarten precies blijven staan waar ze
              stonden. */}
          <div className="flex flex-col gap-3 lg:-m-2 lg:h-[calc(100svh-14rem)] lg:overflow-y-auto lg:p-2">
            {rechts.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>Nog niemand die kan begeleiden.</EmptyTitle>
                </EmptyHeader>
              </Empty>
            ) : (
              rechts.map((kaart) => (
                <BuddyKaart
                  drempel={drempel}
                  eigen={magAlles || kaart.buddy.id === user?.id}
                  kaart={kaart}
                  key={kaart.buddy.id}
                  nu={nu}
                  onKies={(p) => setKiezer({ soort: 'mens', persoon: p })}
                  onKoppel={() => setKiezer({ soort: 'buddy', persoon: kaart.buddy })}
                  onLos={laatLos}
                  onLosTe={setLosTe}
                  onPak={pak}
                  onSleepOver={setOver}
                  onZetNeer={zetNeer}
                  over={over === kaart.buddy.id}
                  sleept={sleept}
                  vrij={gesleepte ? magNaar(gesleepte, kaart.buddy) : null}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <AlertDialog onOpenChange={(open) => !open && setLosTe(null)} open={losTe !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {losTe?.naam} loskoppelen van {losTe?.buddy?.naam}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              De koppeling wordt beëindigd en blijft in de historie staan. {losTe?.naam} komt bij
              Nog geen buddy te staan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (losTe?.koppelingId) beeindig.mutate(losTe.koppelingId);
                setLosTe(null);
              }}
              variant="destructive"
            >
              Loskoppelen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <KiesDialog
        kandidaten={kandidatenVoor(kiezer, personen, alleKaarten, magNaar)}
        kiezer={kiezer}
        onKies={(mensId, buddyId) => {
          setKiezer(null);
          koppel.mutate({ buddyId, medewerkerId: mensId });
        }}
        onSluit={() => setKiezer(null)}
      />
    </div>
  );
}

/** Van welke kant het koppelen begint: bij een mens die een buddy zoekt, of bij
 *  een buddy die er iemand bij krijgt. Hetzelfde venster, andere lijst. */
type Kiezer = { soort: 'mens' | 'buddy'; persoon: PersoonRij };

interface Kandidaat {
  id: string;
  naam: string;
  regel: string;
  bij: string;
  /** Wie er straks in de koppeling aan welke kant staat. */
  mensId: string;
  buddyId: string;
}

function kandidatenVoor(
  kiezer: Kiezer | null,
  personen: readonly PersoonRij[],
  rijen: readonly Kaart[],
  magNaar: (mens: PersoonRij, buddy: PersoonRij) => boolean,
): Kandidaat[] {
  if (!kiezer) return [];
  if (kiezer.soort === 'mens') {
    return rijen
      .filter((k) => magNaar(kiezer.persoon, k.buddy))
      .map((k) => ({
        id: k.buddy.id,
        naam: k.buddy.naam,
        regel: regelVan(k.buddy),
        bij: k.gecoachten.length === 0 ? 'begeleidt niemand' : `begeleidt ${k.gecoachten.length}`,
        mensId: kiezer.persoon.id,
        buddyId: k.buddy.id,
      }));
  }
  return personen
    .filter((p) => magNaar(p, kiezer.persoon))
    .map((p) => ({
      id: p.id,
      naam: p.naam,
      regel: regelVan(p),
      bij: p.buddy ? `onder ${p.buddy.naam}` : '',
      mensId: p.id,
      buddyId: kiezer.persoon.id,
    }));
}

/** Waar iemand zit: de opdrachtgevers zonder dubbele, want twee opdrachten bij
 *  dezelfde klant is één plek. */
function waar(p: PersoonRij): string {
  const gevers = [...new Set(p.opdrachten.map((o) => o.opdrachtgever))];
  return opdrachtenTekst(gevers.map((naam) => ({ naam })));
}

function regelVan(p: PersoonRij): string {
  return `${p.niveau ?? 'niveau niet ingevuld'} · ${waar(p)}`;
}

function Kop({ titel, aantal }: { titel: string; aantal: number }) {
  return (
    <div className="flex items-center gap-2">
      <h2 className="font-heading text-base leading-snug font-medium">{titel}</h2>
      <Badge variant="secondary">{aantal}</Badge>
    </div>
  );
}

/**
 * Eén van de twee lijsten links.
 *
 * Dit waren twee groepen binnen één kaart, met een streep ertussen. Als losse
 * kaarten is het duidelijker dat het twee verschillende dingen zijn: het ene is
 * een werklijst die leeg hoort te raken, het andere is wat er al staat.
 */
/**
 * De naam als weg naar de persoonspagina.
 *
 * `draggable={false}` omdat een browser een link van zichzelf versleept: zonder
 * dit sleep je het webadres in plaats van de rij. Het slepen begint dan bij de
 * rij eromheen, en die is wél te slepen.
 *
 * `stopPropagation` omdat een klik op de rij de buddykiezer opent. Op de naam
 * klikken is iets anders dan op de rij klikken.
 */
function PersoonLink({ p }: { p: PersoonRij }) {
  // De persoonspagina is bewerken, en dat mag niet bij iedereen. Wie je niet
  // mag wijzigen staat er als naam, zonder weg erheen.
  if (!p.magWijzigen) return <span>{p.naam}</span>;
  return (
    <Link
      className="hover:underline"
      draggable={false}
      onClick={(e) => e.stopPropagation()}
      to={`/personen/${p.id}`}
    >
      {p.naam}
    </Link>
  );
}

function MensRij({
  p,
  sleept,
  onPak,
  onLos,
  onKies,
}: {
  p: PersoonRij;
  sleept: boolean;
  onPak: (e: DragEvent<HTMLElement>) => void;
  onLos: () => void;
  onKies: () => void;
}) {
  return (
    <Item
      className={`cursor-grab active:cursor-grabbing ${sleept ? 'opacity-40' : ''}`}
      draggable
      onClick={onKies}
      onDragEnd={onLos}
      onDragStart={onPak}
      size="xs"
    >
      <GripVerticalIcon className="text-muted-foreground/60 size-3.5 shrink-0" />
      <ItemContent>
        <ItemTitle>
          <PersoonLink p={p} />
        </ItemTitle>
      </ItemContent>
    </Item>
  );
}

function BuddyKaart({
  kaart,
  drempel,
  eigen,
  nu,
  sleept,
  vrij,
  over,
  onPak,
  onLos,
  onSleepOver,
  onZetNeer,
  onLosTe,
  onKies,
  onKoppel,
}: {
  kaart: Kaart;
  drempel: number;
  /** Deze kaart is van jou, of je bent Admin: dan valt er te slepen. */
  eigen: boolean;
  nu: string;
  sleept: string | null;
  /** `null` als er niets gesleept wordt, anders of deze kaart het doel mag zijn. */
  vrij: boolean | null;
  over: boolean;
  onPak: (id: string) => (e: DragEvent<HTMLElement>) => void;
  onLos: () => void;
  onSleepOver: (id: string | null) => void;
  onZetNeer: (buddy: PersoonRij) => void;
  onLosTe: (p: PersoonRij) => void;
  onKies: (p: PersoonRij) => void;
  onKoppel: () => void;
}) {
  const rand = vrij === null ? '' : !vrij ? 'opacity-45' : over ? DROP_RAAK : DROP_KAN;

  return (
    <Card
      // `shrink-0` want de kolom hiernaast heeft op een breed scherm een vaste
      // hoogte. Een flexkind krimpt dan standaard mee, en omdat de kaart
      // `overflow-hidden` heeft sneed hij zijn eigen rijen eraf: op een breed
      // scherm bleef alleen de kop met de buddy staan.
      className={`shrink-0 transition-colors ${rand}`}
      // `dragleave` vuurt ook bij het oversteken naar een kind. Zonder deze
      // controle knippert de rand aan en uit zolang je over de kaart beweegt.
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        if (over) onSleepOver(null);
      }}
      // Alleen tegenhouden als het mag: dan wijst de muis zelf al aan of deze
      // kaart een doel is.
      onDragOver={(e) => {
        if (!vrij) return;
        e.preventDefault();
        if (!over) onSleepOver(kaart.buddy.id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onZetNeer(kaart.buddy);
        onLos();
      }}
      size="sm"
    >
      <CardContent className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {kaart.buddy.magWijzigen ? (
          <Link
            className="font-heading text-base leading-snug font-medium hover:underline"
            to={`/personen/${kaart.buddy.id}`}
          >
            {kaart.buddy.naam}
          </Link>
        ) : (
          <span className="font-heading text-base leading-snug font-medium">
            {kaart.buddy.naam}
          </span>
        )}
        {kaart.buddy.buddy && (
          <span className="text-muted-foreground min-w-0 flex-1 truncate text-sm">
            zelf begeleid door {kaart.buddy.buddy.naam}
          </span>
        )}
        {eigen && (
          <Button
            aria-label={`Iemand koppelen aan ${kaart.buddy.naam}`}
            className="ml-auto"
            onClick={onKoppel}
            size="icon-sm"
            variant="ghost"
          >
            <PlusIcon />
          </Button>
        )}
      </CardContent>

      <CardContent>
        {kaart.gecoachten.length === 0 ? (
          <div className="text-muted-foreground flex h-11 items-center justify-center rounded-lg border border-dashed text-sm">
            Begeleidt niemand
          </div>
        ) : (
          <ItemGroup>
            {kaart.gecoachten.map((p) => (
              <Item
                className={`${eigen ? 'cursor-grab active:cursor-grabbing' : ''} ${sleept === p.id ? 'opacity-40' : ''}`}
                draggable={eigen}
                key={p.id}
                onClick={() => eigen && onKies(p)}
                onDragEnd={onLos}
                onDragStart={onPak(p.id)}
                size="sm"
                variant="muted"
              >
                <GripVerticalIcon className="text-muted-foreground/60 size-3.5 shrink-0" />
                <ItemContent>
                  <ItemTitle>
                    <PersoonLink p={p} />
                  </ItemTitle>
                  <ItemDescription>{regelVan(p)}</ItemDescription>
                </ItemContent>
                <Stilte drempel={drempel} nu={nu} persoon={p} />
                {eigen && (
                  <Button
                    aria-label={`${p.naam} loskoppelen`}
                    // Zonder lopende koppeling is er niets om te beëindigen. Kan
                    // niet voorkomen zolang de rij een buddy heeft, maar een knop
                    // die stil niets doet is erger dan een knop die uit staat.
                    disabled={!p.koppelingId}
                    onClick={(e) => {
                      e.stopPropagation();
                      onLosTe(p);
                    }}
                    size="icon-xs"
                    variant="ghost"
                  >
                    <XIcon />
                  </Button>
                )}
              </Item>
            ))}
          </ItemGroup>
        )}
      </CardContent>
    </Card>
  );
}

/** Het signaal bij de persoon die begeleid wordt. Geen uitleg erbij: de tekst
 *  "14 weken" naast een driehoekje zegt genoeg, en de drempel zelf staat bij de
 *  instellingen. */
function Stilte({ persoon, drempel, nu }: { persoon: PersoonRij; drempel: number; nu: string }) {
  const stil = staatStil(persoon, drempel, nu);
  return (
    <Badge className="shrink-0" variant={stil ? 'destructive' : 'secondary'}>
      {stil && <AlertTriangleIcon data-icon="inline-start" />}
      {stilteTekst(persoon, nu)}
    </Badge>
  );
}

/** De weg zonder slepen: op een telefoon en met het toetsenbord. Vanuit een
 *  mens kies je een buddy, vanuit een kaart kies je een mens. */
function KiesDialog({
  kiezer,
  kandidaten,
  onKies,
  onSluit,
}: {
  kiezer: Kiezer | null;
  kandidaten: Kandidaat[];
  onKies: (mensId: string, buddyId: string) => void;
  onSluit: () => void;
}) {
  const [zoek, setZoek] = useState('');
  const gevonden = kandidaten.filter((k) => k.naam.toLowerCase().includes(zoek.toLowerCase()));

  return (
    <Dialog onOpenChange={(open) => !open && onSluit()} open={kiezer !== null}>
      <DialogContent className="sm:max-w-md" onOpenAutoFocus={() => setZoek('')}>
        <DialogHeader>
          <DialogTitle>
            {kiezer?.soort === 'mens'
              ? `Buddy kiezen voor ${kiezer.persoon.naam}`
              : `Koppelen aan ${kiezer?.persoon.naam}`}
          </DialogTitle>
        </DialogHeader>

        <Input
          aria-label="Zoek een naam"
          autoComplete="off"
          onChange={(e) => setZoek(e.target.value)}
          placeholder="Zoek een naam"
          value={zoek}
        />

        <div className="max-h-72 overflow-y-auto rounded-md border p-1">
          {gevonden.length === 0 ? (
            <p className="text-muted-foreground p-2 text-sm">Niemand gevonden.</p>
          ) : (
            gevonden.map((k) => (
              <button
                className="hover:bg-muted/50 flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left"
                key={k.id}
                onClick={() => onKies(k.mensId, k.buddyId)}
                type="button"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{k.naam}</span>
                  <span className="text-muted-foreground block truncate text-xs">{k.regel}</span>
                </span>
                {k.bij && <span className="text-muted-foreground shrink-0 text-xs">{k.bij}</span>}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
